import { Component, HostListener, Inject, Injector, OnInit } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { Environment } from "@digic/appstarter";
import { ResponsiveService } from "@digic/layout";
import { AppComponentBase, ApplicationService, EmployeeServiceBase, UserSettingsService } from "@digic/shared";
import { TranslateService } from "@ngx-translate/core";
import { CONFIG_DATA } from "src/configData";
import { CreateRequestsPayload, RequestsCollectionOption, SuccessResponse } from "src/types";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent extends AppComponentBase implements OnInit {
  public get maintenanceDate(): Date {
    return this.applicationService.maintenanceDate;
  }

  public get windowHeight(): number {
    return this.responsiveService.windowHeight;
  }

  public get windowWidth(): number {
    return this.responsiveService.windowWidth;
  }

  public get xl(): number {
    return this.responsiveService.xl;
  }

  public get md(): number {
    return this.responsiveService.md;
  }

  public get hosts() {
    return this.userSettingsService.myHosts;
  }

  public user: any = [];
  public langImgPath: string;
  public checked: boolean;
  public collectionOptions: Array<RequestsCollectionOption> = [];
  public writeUsForm: UntypedFormGroup;
  public currentDate = new Date();
  public isMaintenanceIncoming: boolean;
  public isUnderMaintenance: boolean;
  public environment: Environment | undefined;
  public avatar: any;

  private static readonly jsonHeaders = new HttpHeaders({ Accept: "application/json" });
  public static readonly itHelpdeskCollectionHandle = "collection575";

  constructor(
    injector: Injector,
    private employeeServiceBase: EmployeeServiceBase,
    private applicationService: ApplicationService,
    private http: HttpClient,
    private formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
    private responsiveService: ResponsiveService,
    private router: Router,
    private userSettingsService: UserSettingsService,
    @Inject(CONFIG_DATA) public configData: any
  ) {
    super(injector);
    this.environment = configData;
  }

  ngOnInit(): void {
    this.getAdditionalUserInfo();
    this.createWriteUsForm();
    this.loadRequestCollections();
    this.getMaintenanceDate();
    this.userSettingsService.getHosts();
    // this.userSettingsService.myHosts = this.userSettingsService.dummyhosts;

    this.employeeServiceBase.getAvatar(this.authenticationService.currentUserAccount.dpc_handle).then((image) => {
      this.avatar = image;
    });
  }

  /**
   * onSubmit
   */
  public onSubmit() {
    if (this.writeUsForm.invalid) {
      this.writeUsForm.markAllAsTouched();
      return;
    }

    const node: CreateRequestsPayload = {
      collectionHandle: this.writeUsForm.value.collectionHandle,
      name: this.writeUsForm.value.subject,
      comment: this.writeUsForm.value.message,
    };

    this.postWriteUs(node);

    this.writeUsForm.patchValue({
      subject: "",
      message: "",
});
  }

  /**
   * getAdditionalUserInfo
   */
  public getAdditionalUserInfo() {
    this.employeeServiceBase.getUserByHandle(this.authenticationService.currentUserAccount.dpc_handle).subscribe((rslt) => {
      this.user = rslt;
    });
  }

  public getMaintenanceDate() {
    this.applicationService.maintenance().subscribe(() => {
      this.isMaintenanceIncoming = this.maintenanceDate && this.currentDate.getTime() < this.maintenanceDate.getTime();
      this.isUnderMaintenance =
        this.maintenanceDate && this.maintenanceDate.getTime() + 3600 * 1000 > this.currentDate.getTime() && !this.isMaintenanceIncoming;
    });
  }

  public changeLocale(locale: string): void {
    this.localizationService.changeLocale(locale);
  }

  public getLangs(): string[] {
    return this.localizationService.getLangs();
  }

  public logout(): void {
    this.authenticationService.logout();
  }

  private getTicketsBaseUrl(): string {
    const dProjectBase = (this.configData?.apiEndpoints?.dProjectBase ?? "").trim();
    if (!dProjectBase) {
      throw new Error("Missing CONFIG_DATA.apiEndpoints.dProjectBase");
    }

    const normalizedBase = dProjectBase.replace(/\/+$/, "");
    return `${normalizedBase}/v1/tickets`;
  }

  private isHtmlFallbackResponse(error: HttpErrorResponse): boolean {
    const body = error?.error;
    const text = typeof body === "string" ? body : typeof body?.text === "string" ? body.text : "";

    return text.trimStart().toLowerCase().startsWith("<!doctype html");
  }

  private logRoutingFailure(url: string, error: HttpErrorResponse): boolean {
    if (!this.isHtmlFallbackResponse(error)) {
      return false;
    }

    console.error(
      `API gateway returned HTML instead of JSON for "${url}". The path is most likely not exposed by the gateway.`,
      error
    );

    return true;
  }

private loadRequestCollections() {
  const url = `${this.getTicketsBaseUrl()}/requests/collections`;

  this.http
    .get<SuccessResponse<RequestsCollectionOption[]>>(
      url,
      { headers: DashboardComponent.jsonHeaders }
    )
    .subscribe({
      next: (response) => {
        this.collectionOptions = response?.data ?? [];

        this.writeUsForm.patchValue({
          collectionHandle: DashboardComponent.itHelpdeskCollectionHandle,
        });
      },
      error: (error: HttpErrorResponse) => {
        if (this.logRoutingFailure(url, error)) {
          return;
        }
      },
    });
}

  private postWriteUs(node: CreateRequestsPayload) {
    const url = `${this.getTicketsBaseUrl()}/requests`;

    this.http.post<SuccessResponse<any>>(url, node, { headers: DashboardComponent.jsonHeaders }).subscribe({
      next: () => {
        this.translate.get("dapp.dashboard.write-us.notification.text").subscribe((localizedText) => {
          this.notificationService.show(
            localizedText,
            "none",
            "Ticket sent!",
            this.router.url,
            "dproject-app/src/app/components/dashboard/dashboard.component.ts/postWriteUs()"
          );
        });
      },
      error: (error: HttpErrorResponse) => {
        this.logRoutingFailure(url, error);

        this.translate.get("dapp.dashboard.write-us.notification.error").subscribe((localizedText) => {
          this.notificationService.show(
            localizedText,
            "error",
            `Error Message: ${error.message}`,
            this.router.url,
            "dproject-app/src/app/components/dashboard/dashboard.component.ts/postWriteUs()"
          );
        });
      },
    });
  }

  private createWriteUsForm() {
    this.writeUsForm = this.formBuilder.group({
      collectionHandle: ["", Validators.required],
      subject: ["", Validators.required],
      message: ["", Validators.required],
    });
  }
}